import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlatformLayout } from "./platform-layout";

interface SecretStatus {
  label: string;
  id: string;
  source: string;
  location: "env" | "db";
  primaryPresent: boolean;
  nextPresent: boolean;
  state: "missing" | "present" | "present (rotating)";
  hotSwap: boolean;
}

interface SecretsResponse {
  secrets: SecretStatus[];
  excludedExternalReferences?: Array<{ source: string; runbook: string }>;
  runbookPath: string;
}

function StateBadge({ state }: { state: SecretStatus["state"] }) {
  if (state === "missing") {
    return (
      <Badge variant="destructive" className="gap-1" data-testid={`badge-state-missing`}>
        <XCircle className="w-3 h-3" /> missing
      </Badge>
    );
  }
  if (state === "present (rotating)") {
    return (
      <Badge variant="secondary" className="gap-1" data-testid={`badge-state-rotating`}>
        <RefreshCw className="w-3 h-3" /> present (rotating)
      </Badge>
    );
  }
  return (
    <Badge variant="default" className="gap-1" data-testid={`badge-state-present`}>
      <CheckCircle2 className="w-3 h-3" /> present
    </Badge>
  );
}

export function SecretsContent() {
  const { data, isLoading, error } = useQuery<SecretsResponse>({
    queryKey: ["/api/platform/secrets/status"],
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-500" />
        <div>
          <h2 className="text-lg font-semibold" data-testid="heading-secrets">Secret Rotation Status</h2>
          <p className="text-sm text-muted-foreground">
            Presence-only inventory of every third-party signing secret. Values are never returned by the API.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tracked secrets</CardTitle>
          <CardDescription>
            See <code className="text-xs">docs/secret-rotation-runbook.md</code>{" "}
            for the per-provider rotation flow. Hot-swap rows accept a{" "}
            <code className="text-xs">*_NEXT</code> env var (or{" "}
            <code className="text-xs">_next</code> DB column) for zero-downtime
            rotation. Single-active rows (no Hot-swap badge) require a brief
            restart — do not set <code className="text-xs">*_NEXT</code> for
            those, the runtime will not consult it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <div data-testid="text-loading">Loading…</div>}
          {error && (
            <div className="text-destructive text-sm" data-testid="text-error">
              Failed to load secret status.
            </div>
          )}
          {data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-secrets">
                <thead>
                  <tr className="text-left border-b">
                    <th className="py-2 pr-4">Secret</th>
                    <th className="py-2 pr-4">Source</th>
                    <th className="py-2 pr-4">Location</th>
                    <th className="py-2 pr-4">Rotation</th>
                    <th className="py-2 pr-4">State</th>
                  </tr>
                </thead>
                <tbody>
                  {data.secrets.map((s) => (
                    <tr key={s.id} className="border-b" data-testid={`row-secret-${s.id}`}>
                      <td className="py-2 pr-4 font-medium">{s.label}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{s.source}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{s.location}</Badge>
                      </td>
                      <td className="py-2 pr-4">
                        {s.hotSwap ? (
                          <Badge variant="secondary" data-testid={`badge-hotswap-${s.id}`}>Hot-swap</Badge>
                        ) : (
                          <Badge variant="outline" data-testid={`badge-singleactive-${s.id}`}>Single-active</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <StateBadge state={s.state} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.excludedExternalReferences && data.excludedExternalReferences.length > 0 && (
                <div className="mt-4 text-xs text-muted-foreground" data-testid="text-excluded-refs">
                  <span className="font-semibold">Not rotated here:</span>{" "}
                  {data.excludedExternalReferences.map((r, i) => (
                    <span key={r.source}>
                      {i > 0 && ", "}
                      <code>{r.source}</code> (see <code>{r.runbook}</code>)
                    </span>
                  ))}
                  .
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlatformSecrets() {
  return <PlatformLayout><SecretsContent /></PlatformLayout>;
}
